import { LoaderData } from "~/types";
import { FormattedLeaseData } from "~/types/lease";
import { EMPTY_LEASE_INFO, getLeaseInfo } from "./getLeaseInfo";
import { getApplicationInfo } from "./getApplicationInfo";
import { getBackgroundCheckInfo } from "./getBackgroundCheckInfo";
import { getMoveExamInfo } from "./getMoveExamInfo";
import { EMPTY_UNIT, getUnitDetails } from "../../unit/getUnitDetails";
import { getParcelDetails } from "../../parcel/getParcelDetails";
import { getOfferInfo } from "./getOfferInfo";
import * as APITypes from "~/API";
import { EMPTY_TENANT_INFO, getTenantInfo } from "../../tenant";

const EMPTY_LEASE: FormattedLeaseData = {
  leaseInfo: EMPTY_LEASE_INFO,
  tenantInfo: EMPTY_TENANT_INFO,
  entityType: APITypes.EntityType.Unit,
  entityDetails: EMPTY_UNIT,
  advanceLeaseAction: "_Review_Application",
  cancelLeaseAction: "_Deny_Application",
};

export const getFormattedLease = async ({
  leaseID,
  leaseData,
}: {
  leaseID?: string;
  leaseData?: LoaderData<APITypes.Lease>;
}): Promise<LoaderData<FormattedLeaseData>> => {
  let leaseInfo = leaseID ? await getLeaseInfo(leaseID) : leaseData;
  if (leaseInfo?.error || !leaseInfo) {
    console.warn("get formatted lease error:", leaseInfo?.error);
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get lease info failed", leaseInfo?.error as Error),
    };
  }

  var lease: FormattedLeaseData = {
    leaseInfo: leaseInfo.data,
    tenantInfo: EMPTY_TENANT_INFO,
    entityType: leaseInfo.data.entityType as APITypes.EntityType,
    entityDetails: EMPTY_UNIT,
    advanceLeaseAction: "_Review_Application",
    cancelLeaseAction: "_Deny_Application",
  };

  if (lease.entityType === APITypes.EntityType.Unit) {
    const unitDetails = await getUnitDetails(leaseInfo.data.entityID);
    lease.entityDetails = unitDetails.data;
  } else if (lease.entityType === APITypes.EntityType.Parcel) {
    const parcelDetails = await getParcelDetails(leaseInfo.data.entityID);
    lease.entityDetails = parcelDetails.data;
  }

  const tenant = await getTenantInfo({
    tenantID: leaseInfo.data.tenantID || "",
  });
  lease.tenantInfo = tenant.data;

  const application = await getApplicationInfo(
    leaseInfo.data.applicationID || ""
  );
  if (application.error) {
    console.warn("application info error:", application.error);
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get application info failed", application.error),
    };
  }

  lease.application = application.data;
  if (application.data.status === "UnderReview") {
    lease.advanceLeaseAction = "_Approve_Application";
  }

  const background = await getBackgroundCheckInfo(
    leaseInfo.data.backgroundCheckID || ""
  );
  if (background.error) {
    console.log("background ID", leaseInfo.data.backgroundCheckID);
    console.log("get background info failed", background.error);
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get background info failed", background.error),
    };
  } else {
    lease.background = background.data;

    if (
      background.data.status === APITypes.BackgroundCheckStatusType.InProgress
    ) {
      lease.advanceLeaseAction = "_Approve_Background";
      lease.cancelLeaseAction = "_Deny_Background";
    }

    if (
      background.data.status === APITypes.BackgroundCheckStatusType.Approved
    ) {
      lease.advanceLeaseAction = "_Send_Offer";
      lease.cancelLeaseAction = "_Cancel_Offer";
    }
  }

  const offer = await getOfferInfo(leaseInfo.data.offerID || "");
  if (offer.error) {
    console.warn("get offer info error:", offer.error);
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get offer info failed", offer.error),
    };
  } else {
    lease.offer = offer.data;

    if (
      offer.data.status === APITypes.OfferStatusType.TenantComplete ||
      offer.data.status === APITypes.OfferStatusType.InProgress
    ) {
      lease.advanceLeaseAction = "_Validate_Offer";
      lease.cancelLeaseAction = "_Cancel_Offer";
    }

    if (
      offer.data.status === APITypes.OfferStatusType.Completed ||
      offer.data.status === APITypes.OfferStatusType.LandlordComplete
    ) {
      lease.advanceLeaseAction = "_Start_MoveIn";
      lease.cancelLeaseAction = "_Cancel_MoveIn";
    }
  }

  const moveIn = await getMoveExamInfo(leaseInfo.data.moveInID || "");
  if (moveIn.error) {
    console.warn("get move in info error:", offer.error);
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get move in info failed", moveIn.error),
    };
  } else {
    lease.moveIn = moveIn.data;
    if (moveIn.data.status === APITypes.MoveExamStatusType.InProgress) {
      lease.advanceLeaseAction = "_Complete_MoveIn";
      lease.cancelLeaseAction = "_Cancel_MoveIn";
    }

    if (moveIn.data.status === APITypes.MoveExamStatusType.Completed) {
      lease.advanceLeaseAction = "_Start_MoveOut";
      lease.cancelLeaseAction = "_Cancel_MoveOut";
    }
  }

  const moveOut = await getMoveExamInfo(leaseInfo.data.moveOutID || "");
  if (moveOut.error) {
    console.warn("get move out info error:", offer.error);
    return {
      success: false,
      data: EMPTY_LEASE,
      error: new Error("get move out info failed", moveOut.error),
    };
  } else {
    lease.moveOut = moveOut.data;
    if (moveOut.data.status === APITypes.MoveExamStatusType.InProgress) {
      lease.advanceLeaseAction = "_Complete_MoveOut";
      lease.cancelLeaseAction = "_Cancel_MoveOut";
    }

    if (
      moveOut.data.status === APITypes.MoveExamStatusType.Completed ||
      moveOut.data.status === APITypes.MoveExamStatusType.Failed
    ) {
      lease.leaseInfo.status = APITypes.LeaseStatusType.Expired;
    }
  }

  return {
    success: true,
    data: lease,
  };
};
